/**
 * GradesGrid — keyboard-navigable IE/DS scores table.
 *
 * Columns: IE (Interrogation Écrite) | DS (Devoir Surveillé)
 * Rows: max(n_ie, n_ds) — each row represents a numbered evaluation slot.
 *
 * Props:
 *   evaluations  {Array}   existing evaluation objects from the API
 *   config       {Object}  {n_ie, n_ds, ie_weight, ds_weight} — null until loaded
 *   subjectId    {number}
 *   periodId     {number}
 *   onSaveCell   {(params)=>Promise<{ok,fieldErrors,userMessage}>}
 *   onSuccess    {()=>void}  called after each successful save (for toast)
 */

import { useRef, useState, useCallback } from 'react';
import GradeCell from './GradeCell';
import { nextCell, cellId } from '../../utils/gridNavigation';

// Default grid size when no config is loaded
const DEFAULT_N_IE = 3;
const DEFAULT_N_DS = 2;
const COL_COUNT = 2; // 0=IE, 1=DS

const GradesGrid = ({ evaluations, config, subjectId, periodId, onSaveCell, onSuccess }) => {
  const nIe = config?.n_ie ?? DEFAULT_N_IE;
  const nDs = config?.n_ds ?? DEFAULT_N_DS;
  const rowCount = Math.max(nIe, nDs);

  // cellValues: { 'IE-0': '12', 'DS-1': '', ... }
  // Initialised lazily from evaluations prop
  const buildInitialValues = useCallback(() => {
    const vals = {};
    for (let i = 0; i < rowCount; i++) {
      vals[`IE-${i}`] = '';
      vals[`DS-${i}`] = '';
    }
    // Derive values by type then slot index
    const ieList = (evaluations || []).filter((e) => e.type === 'IE');
    const dsList = (evaluations || []).filter((e) => e.type === 'DS');
    ieList.forEach((ev, idx) => {
      if (idx < nIe) vals[`IE-${idx}`] = String(ev.score ?? '');
    });
    dsList.forEach((ev, idx) => {
      if (idx < nDs) vals[`DS-${idx}`] = String(ev.score ?? '');
    });
    return vals;
  }, [evaluations, rowCount, nIe, nDs]);

  const [values, setValues] = useState(buildInitialValues);
  const [cellErrors, setCellErrors] = useState({});
  const [savingCells, setSavingCells] = useState({});

  // Rebuild values when evaluations or config change
  const prevEvalsRef = useRef(null);
  if (prevEvalsRef.current !== evaluations) {
    prevEvalsRef.current = evaluations;
    const fresh = buildInitialValues();
    setValues(fresh);
    setCellErrors({});
  }

  // Refs matrix: refs[row][col]
  const cellRefs = useRef({});
  const getRef = (row, col) => {
    const key = `${row}-${col}`;
    if (!cellRefs.current[key]) cellRefs.current[key] = { current: null };
    return cellRefs.current[key];
  };

  const focusCell = (row, col) => {
    const ref = cellRefs.current[`${row}-${col}`];
    if (ref?.current) ref.current.focus();
  };

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    if (cellErrors[key]) setCellErrors((prev) => ({ ...prev, [key]: null }));
  };

  const handleCommit = useCallback(async (row, col) => {
    const type = col === 0 ? 'IE' : 'DS';
    const maxN = col === 0 ? nIe : nDs;
    if (row >= maxN) return; // cell disabled (row beyond this type's count)

    const key = `${type}-${row}`;
    const rawVal = values[key];
    if (rawVal === '' || rawVal === undefined) return; // empty — skip save

    const score = parseFloat(rawVal);
    if (isNaN(score)) return;

    // Find existing evaluation for this slot
    const ieList = (evaluations || []).filter((e) => e.type === 'IE');
    const dsList = (evaluations || []).filter((e) => e.type === 'DS');
    const list = col === 0 ? ieList : dsList;
    const existing = list[row] ?? null;

    setSavingCells((prev) => ({ ...prev, [key]: true }));
    const result = await onSaveCell({
      evaluationId: existing?.id ?? null,
      type,
      score,
      label: null,
      evaluatedOn: null,
    });
    setSavingCells((prev) => ({ ...prev, [key]: false }));

    if (!result.ok) {
      const fieldKey = Object.keys(result.fieldErrors || {})[0];
      const msg = fieldKey
        ? result.fieldErrors[fieldKey]
        : result.userMessage;
      setCellErrors((prev) => ({ ...prev, [key]: msg }));
    } else {
      setCellErrors((prev) => ({ ...prev, [key]: null }));
      if (onSuccess) onSuccess();
    }
  }, [values, evaluations, nIe, nDs, onSaveCell, onSuccess]);

  const handleKeyDown = useCallback((row, col, e) => {
    const navigableKeys = ['Tab', 'Enter', 'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Escape'];
    if (!navigableKeys.includes(e.key)) return;

    if (e.key === 'Escape') {
      e.target.blur();
      return;
    }

    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      // Commit current cell, then move
      const type = col === 0 ? 'IE' : 'DS';
      const key = `${type}-${row}`;
      const rawVal = values[key];
      if (rawVal !== '' && rawVal !== undefined) {
        handleCommit(row, col);
      }
      const target = nextCell({ row, col }, e.key, e.shiftKey, rowCount, COL_COUNT);
      if (target) focusCell(target.row, target.col);
      return;
    }

    // Arrow keys
    e.preventDefault();
    const target = nextCell({ row, col }, e.key, e.shiftKey, rowCount, COL_COUNT);
    if (target) focusCell(target.row, target.col);
  }, [values, handleCommit, rowCount]);

  // Check if a cell slot is within bounds for that type
  const isActive = (row, col) => {
    if (col === 0) return row < nIe;
    return row < nDs;
  };

  const colLabels = ['IE', 'DS'];

  return (
    <div className="overflow-x-auto">
      <table className="border-collapse" aria-label="Grille de notes">
        <thead>
          <tr>
            <th className="px-3 py-2 text-left text-sm font-montserrat font-semibold text-[#4A4A4A] border-b border-gray-200">
              N°
            </th>
            {colLabels.map((label) => (
              <th
                key={label}
                className="px-3 py-2 text-center text-sm font-montserrat font-semibold text-[#17386E] border-b border-gray-200"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rowCount }, (_, row) => (
            <tr key={row} className="hover:bg-[#EAF4FB]/30">
              <td className="px-3 py-2 text-sm font-montserrat text-[#4A4A4A]">
                {row + 1}
              </td>
              {[0, 1].map((col) => {
                const type = col === 0 ? 'IE' : 'DS';
                const key = `${type}-${row}`;
                const active = isActive(row, col);
                return (
                  <td
                    key={col}
                    className="px-3 py-2 text-center"
                  >
                    {active ? (
                      <GradeCell
                        cellRef={getRef(row, col)}
                        value={values[key] ?? ''}
                        error={cellErrors[key] ?? null}
                        saving={savingCells[key] ?? false}
                        onChange={(val) => handleChange(key, val)}
                        onCommit={() => handleCommit(row, col)}
                        onKeyDown={(e) => handleKeyDown(row, col, e)}
                        data-cell-id={cellId(row, col)}
                        aria-label={`Note ${type} n°${row + 1}`}
                        placeholder="—"
                      />
                    ) : (
                      <span className="text-gray-300 text-sm">—</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default GradesGrid;
